import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Fab,
} from '@mui/material';
import {
  Save,
  ArrowForward,
  Refresh as RefreshIcon,
  CheckCircle,
  Warning,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useUnifiedSchema } from '../hooks/useUnifiedSchema';
import { ViewContext, viewContextFromTabIndex } from '../hooks/useSchemaChatState';
import UnifiedSchemaView from './schema-view/UnifiedSchemaView';
import SchemaChatDrawer, { DRAWER_WIDTH } from './schema-chat/SchemaChatDrawer';

const blinkAnimation = {
  animation: 'pageFlash 450ms ease-in-out',
  '@keyframes pageFlash': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.3 },
    '100%': { opacity: 1 },
  },
};

const ProfileViewer: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [viewContext, setViewContext] = useState<ViewContext | undefined>(undefined);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [blinkKey, setBlinkKey] = useState(0);

  const schema = useUnifiedSchema(batchId!);

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setBlinkKey((k) => k + 1);

    try {
      await refetch();
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 350);
    }
  };

  const {
    isLoading,
    isSaving,
    hasChanges,
    semanticError,
    saveMutationSuccess,
    mappingAlreadyStarted,
    summaries,
    handleSave,
    refetch,
    handleUpdateTable,
    handleUpdateColumn,
    handleTogglePrimaryKey,
    handleUpdateJoins,
  } = schema;

  // Wrap mutation handlers so we can reliably detect real user edits
  const handleUpdateTableWithFlag = useCallback(
    (index: number, field: any, value: string) => {
      setHasUserEdited(true);
      handleUpdateTable(index, field, value);
    },
    [handleUpdateTable],
  );

  const handleUpdateColumnWithFlag = useCallback(
    (tableIndex: number, colIndex: number, updates: any) => {
      setHasUserEdited(true);
      handleUpdateColumn(tableIndex, colIndex, updates);
    },
    [handleUpdateColumn],
  );

  const handleTogglePrimaryKeyWithFlag = useCallback(
    (tableIndex: number, columnName: string, isPk: boolean) => {
      setHasUserEdited(true);
      handleTogglePrimaryKey(tableIndex, columnName, isPk);
    },
    [handleTogglePrimaryKey],
  );

  const handleUpdateJoinsWithFlag = useCallback(
    (tableIndex: number, updatedJoins: any) => {
      setHasUserEdited(true);
      handleUpdateJoins(tableIndex, updatedJoins);
    },
    [handleUpdateJoins],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (semanticError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Failed to load schema data.
      </Alert>
    );
  }

  if (!summaries || summaries.length === 0) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Data Profile & Schema Analysis
        </Typography>
        <Alert severity="info" icon={<Warning />}>
          <Typography variant="subtitle1" gutterBottom>
            Schema Analysis Not Available
          </Typography>
          <Typography variant="body2">
            Semantic summaries will be generated automatically during preprocessing. Please wait for
            the batch to complete profiling, or check if there was an error during processing.
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetch()} sx={{ mt: 1 }}>
            Refresh
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        transition: 'width 225ms cubic-bezier(0.4, 0, 0.2, 1)',
        width: chatOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
        height: '100%',
        pl: 2,
        marginRight: 0,
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Data Profile & Schema Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and edit semantic classifications for your data. Changes affect how mappings are
            generated.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          {/* <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isSaving}
          >
            Refresh
          </Button> */}
          <Button
            variant="outlined"
            startIcon={
              isRefreshing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RefreshIcon />
              )
            }
            onClick={handleRefresh}
            disabled={isSaving || isRefreshing}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          {activeTabIndex !== 3 && (
            <Button
              variant="outlined"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={() => navigate(`/batches/${batchId}/workspace`)}
          >
            Open Workspace
          </Button>
        </Stack>
      </Box>

      {/* Alerts */}
      {hasChanges && hasUserEdited && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Save before proceeding or they will be lost.
        </Alert>
      )}

      {saveMutationSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          Changes saved successfully.
        </Alert>
      )}

      {/* Unified Schema View */}
      <Box
        key={blinkKey}
        sx={{
          animation: isRefreshing ? 'pageFlash 350ms ease-in-out' : 'none',
          '@keyframes pageFlash': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.3 },
            '100%': { opacity: 1 },
          },
        }}
      >
        <UnifiedSchemaView
          batchId={batchId!}
          schema={schema}
          onViewChange={(tableIndex, tabIndex) => {
            setActiveTabIndex(tabIndex);
            const table = summaries[tableIndex];
            setViewContext(viewContextFromTabIndex(table, tabIndex));
          }}
        />
      </Box>

      {/* Sticky save bar — visible when there are unsaved changes */}
      {hasChanges && hasUserEdited && activeTabIndex !== 3 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: chatOpen ? DRAWER_WIDTH : 0,
            bgcolor: 'warning.dark',
            color: 'warning.contrastText',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1200,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
            transition: 'right 225ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            You have unsaved changes
          </Typography>
          <Button
            variant="contained"
            color="inherit"
            size="small"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={isSaving}
            sx={{ color: 'warning.dark', bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' } }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}

      {/* Chat FAB */}
      <Fab
        color="primary"
        size="small"
        onClick={() => setChatOpen(!chatOpen)}
        sx={{
          position: 'fixed',
          bottom: hasChanges && hasUserEdited ? 64 : 24,
          right: chatOpen ? DRAWER_WIDTH + 24 : 24,
          transition: 'all 225ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <ChatIcon fontSize="small" />
      </Fab>

      {/* Chat Drawer */}
      <SchemaChatDrawer
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        batchId={batchId!}
        summaries={summaries}
        viewContext={viewContext}
        onTogglePrimaryKey={handleTogglePrimaryKeyWithFlag}
        onUpdateJoins={handleUpdateJoinsWithFlag}
        onUpdateColumn={handleUpdateColumnWithFlag}
        onUpdateTable={handleUpdateTableWithFlag}
        onSchemaModified={refetch}
      />
    </Box>
  );
};

export default ProfileViewer;
